import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { format, subDays } from "date-fns";
import DateModal from "@/pages/dashboard/components/date-modal";
import AnalyticsTable from "@/pages/dashboard/components/analytics-table";
import states from "@/constants/states";
import ExportDashboardAnalyticsButton from "@/pages/dashboard/components/export-dashboard-analytics-button";

function SelectStateDropdown({ selectedState, handleSelectedStateChange }) {
  return (
    <FormControl fullWidth>
      <InputLabel id="demo-simple-select-label">State</InputLabel>
      <Select
        sx={{ border: "1px", outline: "none", boxShadow: "none" }}
        labelId="demo-simple-select-label"
        id="demo-simple-select"
        value={selectedState}
        label="State"
        onChange={handleSelectedStateChange}
        variant="outlined"
      >
        <MenuItem value="All">All</MenuItem>

        {states.map((item, index) => (
          <MenuItem value={item.code} key={index}>
            {item.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const DashboardPage = () => {
  const [isDateModalVisible, setIsDateModalVisible] = useState(false);
  const [selectedState, setSelectedState] = useState("All");

  const [dateState, setDateState] = useState([
    {
      startDate: subDays(new Date(), 30),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  // console.log(dashboardData);

  const handleDateChange = (item) => setDateState([item.selection]);

  const handleSelectedStateChange = (event) => {
    setSelectedState(event.target.value);
  };

  const toggleDateModal = () => {
    setIsDateModalVisible(!isDateModalVisible);
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <DateModal
        open={isDateModalVisible}
        handleClose={toggleDateModal}
        handleDateChange={handleDateChange}
        dateState={dateState}
      />

      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <Typography variant="h4">Dashboard</Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <Button
                sx={{ m: 0 }}
                variant="contained"
                onClick={toggleDateModal}
              >
                {`${format(dateState[0].startDate, "MM/dd/yyyy")} - ${format(
                  dateState[0].endDate,
                  "MM/dd/yyyy"
                )}`}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <SelectStateDropdown
                selectedState={selectedState}
                handleSelectedStateChange={handleSelectedStateChange}
              />
            </Grid>
          </Grid>

          <Stack alignItems="flex-end">
            <ExportDashboardAnalyticsButton
              startDate={dateState[0].startDate}
              endDate={dateState[0].endDate}
              selectedState={selectedState}
            />
          </Stack>

          <Stack spacing={3}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h5" p={2}>
                Price Tracker Views
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="make"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Make Views"
                    selectedState={selectedState}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="model"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Model Views"
                    selectedState={selectedState}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="grade"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Grade Views"
                    selectedState={selectedState}
                  />
                </Grid>
              </Grid>
            </Card>

            <Card sx={{ p: 2 }}>
              <Typography variant="h5" p={2}>
                Price Tracker Ads
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="sponsored-views"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Sponsored Ads Views"
                    selectedState={selectedState}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="sponsored"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Sponsored Ads Clicks"
                    selectedState={selectedState}
                  />
                </Grid>
              </Grid>
            </Card>

            <Card sx={{ p: 2 }}>
              <Typography variant="h5" p={2}>
                Advertisements
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="advertisement-views"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Advertisement Views"
                    selectedState={selectedState}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="advertisement-clicks"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Advertisement Clicks"
                    selectedState={selectedState}
                  />
                </Grid>
              </Grid>
            </Card>

            <Card sx={{ p: 2 }}>
              <Typography variant="h5" p={2}>
                Finance Calculator
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <AnalyticsTable
                    type="finance-clicks"
                    startDate={dateState[0].startDate}
                    endDate={dateState[0].endDate}
                    title="Finance Calculator Clicks"
                    selectedState={selectedState}
                  />
                </Grid>
              </Grid>
            </Card>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default DashboardPage;
